import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Package, Pencil, Plus, Search, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { downloadBarcode, downloadBarcodeSheet, printBarcodes } from "@/lib/barcode";
import { Barcode, Printer, Wand2 } from "lucide-react";

const blank = {
  name: "", sku: "", category: "", model_no: "", description: "", purchase_cost: 0, selling_price: "",
  tax_rate: 18, uom: "Nos", min_stock: 5, barcode: "", image: "", active: true,
};

export const ProductsPanel = ({ markup = 40 }) => {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/products", { params: { q } }), api.get("/categories")]);
      setRows(p.data); setCats(c.data);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const autoPrice = Math.round(Number(form.purchase_cost || 0) * (1 + markup / 100));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      purchase_cost: Number(form.purchase_cost || 0),
      selling_price: form.selling_price === "" ? null : Number(form.selling_price),
      tax_rate: Number(form.tax_rate), min_stock: Number(form.min_stock),
    };
    try {
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(`Product ${editing ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat.trim() });
      toast.success("Category added");
      setNewCat(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    {
      key: "name", label: "Product",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.name}</p>
          <p className="num truncate text-xs text-muted-foreground">{[r.sku, r.model_no && r.model_no !== r.sku ? r.model_no : null, r.barcode].filter(Boolean).join(" . ")}</p>
        </div>
      ),
    },
    { key: "category", label: "Category", render: (r) => <StatusBadge status={r.category} tone="neutral" dot={false} /> },
    { key: "purchase_cost", label: "Cost", align: "right", render: (r) => <span className="num text-sm">{money(r.purchase_cost)}</span> },
    { key: "selling_price", label: "Selling", align: "right", render: (r) => <span className="num text-sm font-semibold">{money(r.selling_price)}</span> },
    { key: "tax_rate", label: "Tax", align: "right", render: (r) => <span className="num text-sm">{r.tax_rate}%</span>, hideMobile: true },
    { key: "min_stock", label: "Min level", align: "right", render: (r) => <span className="num text-sm">{r.min_stock} {r.uom}</span>, hideMobile: true },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Active" : "Inactive"} /> },
    {
      key: "actions", label: "", align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="iconSm" variant="outline" title="Print barcode label" data-testid={`print-bc-${r.sku}`}
            onClick={() => printBarcodes(r, "Megatronix Vision", 1)}>
            <Printer className="h-4 w-4" />
          </Button>
          <Button size="iconSm" variant="outline" title="Download barcode" onClick={() => downloadBarcode(r)}>
            <Barcode className="h-4 w-4" />
          </Button>
          <Button size="iconSm" variant="outline" onClick={() => { setEditing(r); setForm({ ...blank, ...r }); setOpen(true); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="iconSm" variant="outline" onClick={async () => {
            await api.delete(`/products/${r.id}`); toast.success("Product deactivated"); load();
          }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search products by name, SKU or barcode" />
        </div>
        {rows.length > 0 && (
          <>
            <Button variant="outline" onClick={() => printBarcodes(rows, "Megatronix Vision", 1)} data-testid="print-all-bc">
              <Printer className="h-4 w-4" /> Print all stickers
            </Button>
            <Button variant="outline" onClick={() => downloadBarcodeSheet(rows, "Megatronix Vision", 1)} data-testid="download-all-bc">
              <Barcode className="h-4 w-4" /> Download stickers
            </Button>
          </>
        )}
        <Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} data-testid="new-product">
          <Plus className="h-4 w-4" /> Add product
        </Button>
      </div>

      {/* Categories */}
      <div className="rounded-xl border border-border bg-card p-4">
        <p className="flex items-center gap-2 text-sm font-semibold"><Tag className="h-4 w-4 text-primary" /> Product categories</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {cats.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs">
              {c.name}
              <button onClick={async () => { await api.delete(`/categories/${c.id}`); toast.success("Category removed"); load(); }}
                className="text-muted-foreground hover:text-destructive">&times;</button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex max-w-sm gap-2">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="h-9" />
          <Button variant="outline" onClick={addCategory}>Add</Button>
        </div>
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable columns={columns} rows={rows}
          empty={<EmptyState icon={Package} title="No products yet" description="Add your first product preset so it can be used across quotations, invoices and purchase orders." />} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? `Edit ${editing.name}` : "Add product"}</DialogTitle>
            <DialogDescription>
              Leave the selling price empty and it will be calculated as cost + {markup}% markup.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Product name" required>
                <Input required value={form.name} data-testid="product-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="SKU" required hint="Generated from the category. You can overwrite it.">
                <div className="flex gap-2">
                  <Input required value={form.sku} data-testid="product-sku" onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                  <Button type="button" variant="outline" size="icon" title="Generate SKU" data-testid="gen-sku"
                    onClick={async () => {
                      const { data } = await api.get("/products/next-sku", { params: { category: form.category } });
                      setForm((f) => ({ ...f, sku: data.sku }));
                      toast.success(`SKU ${data.sku} generated`);
                    }}>
                    <Wand2 className="h-4 w-4" />
                  </Button>
                </div>
              </Field>
              <Field label="Category" required>
                <Select value={form.category} onValueChange={async (v) => {
                  setForm((f) => ({ ...f, category: v }));
                  if (!editing && !form.sku) {
                    try {
                      const { data } = await api.get("/products/next-sku", { params: { category: v } });
                      setForm((f) => ({ ...f, category: v, sku: data.sku }));
                    } catch { /* ignore */ }
                  }
                }}>
                  <SelectTrigger data-testid="product-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Model number" hint="Printed under the barcode on every sticker. Falls back to the SKU.">
                <Input value={form.model_no || ""} data-testid="product-model"
                  onChange={(e) => setForm({ ...form, model_no: e.target.value })} placeholder={form.sku || "RC-4500"} />
              </Field>
              <Field label="Unit of measurement">
                <Select value={form.uom} onValueChange={(v) => setForm({ ...form, uom: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Nos", "Set", "Box", "Kg", "Metre", "Pair"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Purchase cost (INR)" required>
                <Input type="number" min="0" required value={form.purchase_cost} data-testid="product-cost"
                  onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} />
              </Field>
              <Field label="Selling price (INR)" hint={`Auto: ${money(autoPrice)} at ${markup}% markup`}>
                <Input type="number" min="0" value={form.selling_price} placeholder={String(autoPrice)}
                  onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
              </Field>
              <Field label="Tax rate">
                <Select value={String(form.tax_rate)} onValueChange={(v) => setForm({ ...form, tax_rate: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[0, 5, 12, 18, 28].map((t) => <SelectItem key={t} value={String(t)}>{t}%</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Minimum stock level">
                <Input type="number" min="0" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
              </Field>
              <Field label="Barcode" hint="Left empty, a unique barcode is created automatically and can be printed from the list." className="sm:col-span-2">
                <div className="flex gap-2">
                  <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
                  {editing?.barcode && (
                    <Button type="button" variant="outline" onClick={() => printBarcodes(editing, "Megatronix Vision", 1)}>
                      <Printer className="h-4 w-4" /> Label
                    </Button>
                  )}
                </div>
              </Field>
            </div>
            <Field label="Description">
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="product-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Add product"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
